import { fetchClinicEvents } from '@/entities/clinic';
import { FULL_EVENT_TYPE_OPTIONS } from '@/shared/config';
import {
  showMessageError,
  showMessageInfo,
  adjustStudyTime,
  appearDataTable,
  GETTEXT_ALL_EVENT_TYPE,
} from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useImperativeHandle, useRef } from 'react';
import { t } from 'ttag';
import type { EventTableComponentProps } from './eventTableComponent.types';

const eventTypeList = [GETTEXT_ALL_EVENT_TYPE, ...FULL_EVENT_TYPE_OPTIONS, 'Manual'];

/**
 * Drives the ECG viewer's event table: cursor-paged loading of clinic events for the selected day, event-type filtering and row selection. Keeps the filter, paging and event state, refetches whenever dataFilter changes with a limit derived from the reload and size-per-page flags, resets the cursor when the selected date or study changes, and exposes handleReload through the imperative ref.
 *
 * @param studyFid - Friendly identifier of the study whose events are listed.
 * @param selectedDate - Day whose events are fetched.
 * @param onClickEvent - Callback invoked with the clicked event row.
 * @param updateEventType - Callback notified when the event type filter changes.
 * @param studyTimeOffset - Study time offset used to convert the day bounds to server time.
 * @param ref - Forwarded ref that receives the handleReload handle.
 * @returns The table state with the pagination, row click and dropdown handlers.
 */
export const useEventTableComponent = (
  {
    studyFid,
    selectedDate,
    onClickEvent,
    updateEventType,
    studyTimeOffset,
  }: EventTableComponentProps,
  ref: any,
) => {
  const needFetchMoreEventsNextPage = useRef(false);
  const needFetchMoreEventsSizePerPage = useRef(false);
  const needReloadData = useRef(false);

  const [state, setState] = useMergeState<any>({
    isOpenDropdown: false,
    selectedEventType: eventTypeList[0],
    selectedRow: undefined,
    events: [],
    isEndOfData: false,
    isLoadedData: false,
    page: 1,
    sizePerPage: 10,
    maxDisplayedData: 10,
    dataFilter: {
      cursor: undefined,
      studyFid,
      sortOrder: 'asc',
      eventType: undefined,
      startSearchDate: undefined,
      stopSearchDate: undefined,
    },
  });

  /**
   * Fetches one page of clinic ECG events for the selected study day. Converts the day's start and end into server time through adjustStudyTime, queries with the cloned filter and the given or current page size, and when a cursor is set appends the new events onto the loaded ones while recomputing page, maxDisplayedData and the end-of-data flag, showing a no-more-events notice on an empty append and emptying the list on error.
   *
   * @param limit - Number of events to request, or falsy to use the current page size.
   * @returns Resolves once the fetched events have been merged into state.
   */
  const fetchData = async (limit: any) => {
    const cloneDataFilter = _.cloneDeep(state.dataFilter);
    const startSearchDate = adjustStudyTime({
      dateMoment: selectedDate.startOf('day'),
      studyTimeOffset,
      shouldRevertToServerTime: true,
    }).toISOString();
    const stopSearchDate = adjustStudyTime({
      dateMoment: selectedDate.endOf('day'),
      studyTimeOffset,
      shouldRevertToServerTime: true,
    }).toISOString();
    _.assign(cloneDataFilter, {
      startSearchDate,
      stopSearchDate,
    });

    try {
      const queryFilter = _.assign({}, cloneDataFilter);

      const newEvents = await fetchClinicEvents(queryFilter, limit || state.sizePerPage);

      const setStateObject = {
        isLoadedData: true,
        isEndOfData: newEvents.length < (limit || state.sizePerPage),
      };

      const clonedEvents = _.cloneDeep(state.events);

      if (queryFilter.cursor) {
        const diffEvents = _.differenceBy(newEvents, clonedEvents, 'id');
        const appearedEvents = appearDataTable(clonedEvents, newEvents);
        // *: When change size per page
        if (limit) {
          _.assign(setStateObject, {
            events: appearedEvents,
            maxDisplayedData:
              state.sizePerPage * state.page > state.maxDisplayedData
                ? state.sizePerPage * state.page
                : state.maxDisplayedData,
            isEndOfData: newEvents.length < (limit || state.sizePerPage) || !diffEvents.length, // *: New events is similar to current events
          });
        } else {
          _.assign(setStateObject, {
            events: appearedEvents,
            page: needFetchMoreEventsNextPage.current ? state.page + 1 : state.page,
            maxDisplayedData: needFetchMoreEventsNextPage.current
              ? state.maxDisplayedData + state.sizePerPage
              : state.maxDisplayedData,
            isEndOfData: newEvents.length < (limit || state.sizePerPage) || !diffEvents.length,
          });
        }

        if (_.isEmpty(newEvents) && !_.isEmpty(clonedEvents)) {
          showMessageInfo({ content: t`No more events` });
        }
      } else {
        _.assign(setStateObject, { events: newEvents });
      }

      setState(setStateObject);
    } catch (error) {
      console.error('Failed to fetch events: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({ events: [], isLoadedData: true });
    }

    needFetchMoreEventsNextPage.current = false;
    needFetchMoreEventsSizePerPage.current = false;
  };

  /**
   * Applies a page or page-size change to the ECG viewer event table, fetching further events when the requested range runs past what is loaded. In that case it raises the size-per-page fetch flag and sets the filter cursor to the last loaded event's friendlyId, otherwise it only records page, sizePerPage and maxDisplayedData.
   *
   * @param pagination - Table pagination carrying the requested current page and pageSize.
   * @returns Nothing.
   */
  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination;
    if (current * pageSize > state.events.length && !state.isEndOfData) {
      needFetchMoreEventsSizePerPage.current = true;
      const clonedDataFilter = _.cloneDeep(state.dataFilter);
      _.assign(clonedDataFilter, { cursor: state.events[state.events.length - 1].friendlyId });
      setState({
        isLoadedData: false,
        dataFilter: clonedDataFilter,
        page: current,
        sizePerPage: pageSize,
        maxDisplayedData: current * pageSize,
      });
    } else {
      setState({
        page: current,
        sizePerPage: pageSize,
        maxDisplayedData: current * pageSize,
      });
    }
  };

  /**
   * Refilters the clinic event list by event type, restarting from the first page. Clears the cursor and sets eventType on a cloned filter, mapping the all-types option onto undefined, marks the data as loading and notifies the parent through updateEventType.
   *
   * @param eventType - Event type to filter on, or the all-types option.
   * @returns Nothing.
   */
  const handleChangeEventType = (eventType: any) => {
    const clonedDataFilter = _.cloneDeep(state.dataFilter);
    _.assign(clonedDataFilter, {
      cursor: undefined,
      eventType: eventType === GETTEXT_ALL_EVENT_TYPE ? undefined : eventType,
    });
    setState({ isLoadedData: false, dataFilter: clonedDataFilter });
    updateEventType(eventType);
  };

  /**
   * Selects an event type in the event table's filter dropdown. Stores the option as selectedEventType, which an update effect turns into a refiltered fetch.
   *
   * @param option - Event type option chosen from the dropdown.
   * @returns Nothing.
   */
  const onClickDropdownItem = (option: any) => {
    setState({ selectedEventType: option });
  };

  /**
   * Handles a click on a row of the ECG viewer event table. Highlights the row by storing record.id as selectedRow and forwards the record to the onClickEvent prop.
   *
   * @param record - Event row that was clicked.
   * @returns Nothing.
   */
  const handleOnRow = (record: any) => {
    setState({ selectedRow: record.id });
    onClickEvent(record);
  };

  useImperativeHandle(ref, () => ({
    handleReload: () => {
      needReloadData.current = true;
      const clonedDataFilter = _.cloneDeep(state.dataFilter);
      _.assign(clonedDataFilter, { cursor: undefined });
      setState({ isLoadedData: false, dataFilter: clonedDataFilter });
    },
  }));

  useEffect(() => {
    if (selectedDate) {
      const clonedDataFilter = _.cloneDeep(state.dataFilter);
      _.assign(clonedDataFilter, {
        studyFid,
        cursor: undefined,
      });
      setState({ isLoadedData: false, dataFilter: clonedDataFilter });
    }
  }, [selectedDate]);

  useUpdateEffect(() => {
    handleChangeEventType(state.selectedEventType);
  }, [state.selectedEventType]);

  useUpdateEffect(() => {
    const limit = needReloadData.current
      ? state.sizePerPage * state.page
      : needFetchMoreEventsSizePerPage.current
        ? state.sizePerPage * state.page - state.events.length
        : null;
    fetchData(limit);
  }, [state.dataFilter]);

  return { state, handleTableChange, handleOnRow, onClickDropdownItem };
};

export { eventTypeList };
